/* ==========================================================================
   LUX — Cart drawer, cart page, quick add, quick view

   Everything goes through Shopify's own Cart AJAX API and the Section
   Rendering API, so the server stays the source of truth for prices,
   discounts and inventory. Native /cart and checkout are never bypassed.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ID = 'lux-cart-drawer';
  var body = document.body;

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */

  function routeRoot() {
    // Honour locale/market sub-paths such as /en-gb.
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function url(path) {
    return routeRoot().replace(/\/$/, '') + path;
  }

  function money(cents) {
    // Only used for optimistic UI; the server-rendered markup replaces it.
    return (cents / 100).toFixed(2);
  }

  var toastTimer = null;

  function toast(message, isError) {
    var el = document.querySelector('[data-lux-toast]');
    if (!el) return;

    el.textContent = message;
    el.classList.toggle('lux-toast--error', !!isError);
    el.classList.add('is-visible');

    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      el.classList.remove('is-visible');
    }, 3200);
  }

  function setBusy(button, busy, busyLabel) {
    if (!button) return;

    var label = button.querySelector('[data-label]');

    if (busy) {
      button.setAttribute('aria-busy', 'true');
      if (label) {
        button.dataset.luxPrevLabel = label.textContent;
        label.textContent = busyLabel || 'Adding…';
      }
    } else {
      button.removeAttribute('aria-busy');
      if (label && button.dataset.luxPrevLabel) {
        label.textContent = button.dataset.luxPrevLabel;
        delete button.dataset.luxPrevLabel;
      }
    }
  }

  /* ---------------------------------------------------------------------
     Drawer open / close
     --------------------------------------------------------------------- */

  var drawerReleaseTrap = null;

  function drawerEl() { return document.querySelector('[data-lux-cart-drawer]'); }

  function openDrawer() {
    var el = drawerEl();
    if (!el) { window.location.href = url('/cart'); return; }

    // A menu or search panel may be open — close it first.
    if (window.LuxUI && window.LuxUI.closePanel) window.LuxUI.closePanel();

    body.classList.add('lux-cart-open');
    el.setAttribute('aria-hidden', 'false');

    if (window.LuxUI) {
      window.LuxUI.lockScroll();
      drawerReleaseTrap = window.LuxUI.trapFocus(el, closeDrawer);
    }
  }

  function closeDrawer() {
    var el = drawerEl();
    if (!el || !body.classList.contains('lux-cart-open')) return;

    body.classList.remove('lux-cart-open');
    el.setAttribute('aria-hidden', 'true');

    if (drawerReleaseTrap) { drawerReleaseTrap(); drawerReleaseTrap = null; }
    if (window.LuxUI) window.LuxUI.unlockScroll();
  }

  function drawerLoading(state) {
    var el = drawerEl();
    if (el) el.classList.toggle('is-loading', !!state);
  }

  /* ---------------------------------------------------------------------
     Cart page (standalone /cart) — loading state only; markup swap is
     handled generically by applyCartPageSectionHTML below.
     --------------------------------------------------------------------- */

  function cartPageEl() { return document.querySelector('[data-lux-cart-page]'); }

  function pageLoading(state) {
    var el = cartPageEl();
    if (el) el.classList.toggle('is-loading', !!state);
  }

  /* ---------------------------------------------------------------------
     Which sections to request from the Section Rendering API. The drawer is
     always present (rendered once from layout/theme.liquid); the cart page's
     own section is only present on /cart, so it's detected, not assumed.
     --------------------------------------------------------------------- */

  function extraCartSectionIds() {
    var root = document.querySelector('[data-lux-cart-items-root]');
    return root ? [root.getAttribute('data-section-id')] : [];
  }

  function allCartSectionIds() {
    return [SECTION_ID].concat(extraCartSectionIds());
  }

  /* ---------------------------------------------------------------------
     Re-render the drawer / cart page from the server
     --------------------------------------------------------------------- */

  function paintCartCount(count) {
    document.querySelectorAll('[data-lux-cart-count]').forEach(function (el) {
      var span = el.querySelector('span');
      if (span) span.textContent = count;
      el.hidden = count === 0;
    });

    document.querySelectorAll('[data-lux-cart-toggle]').forEach(function (el) {
      el.setAttribute('aria-label', 'Cart (' + count + ')');
    });
  }

  function applySectionHTML(html) {
    var el = drawerEl();
    if (!el || !html) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-lux-cart-content]');
    var current = el.querySelector('[data-lux-cart-content]');

    if (fresh && current) current.replaceWith(fresh);
  }

  function applyCartPageSectionHTML(sectionId, html) {
    if (!html) return;

    var current = document.querySelector('[data-section-id="' + sectionId + '"]');
    if (!current) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-section-id="' + sectionId + '"]');

    if (fresh) current.replaceWith(fresh);
  }

  function refreshFromResponse(data) {
    if (!data || !data.sections) return;

    Object.keys(data.sections).forEach(function (id) {
      if (id === SECTION_ID) {
        applySectionHTML(data.sections[id]);
      } else {
        applyCartPageSectionHTML(id, data.sections[id]);
      }
    });
  }

  function fetchDrawer() {
    var ids = allCartSectionIds();

    return fetch(url('/?sections=' + ids.join(',')), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data) return;
        ids.forEach(function (id) {
          if (!data[id]) return;
          if (id === SECTION_ID) applySectionHTML(data[id]);
          else applyCartPageSectionHTML(id, data[id]);
        });
      })
      .catch(function () { /* drawer just stays as it was */ });
  }

  function fetchCartState() {
    return fetch(url('/cart.js'), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        paintCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('lux:cart:update', { detail: { cart: cart } }));
        return cart;
      })
      .catch(function () { return null; });
  }

  /* ---------------------------------------------------------------------
     Cart mutations
     --------------------------------------------------------------------- */

  function addToCart(payload, button) {
    setBusy(button, true, 'Adding…');

    return fetch(url('/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(Object.assign({ sections: allCartSectionIds().join(',') }, payload))
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            // Shopify returns { description, message, status } on failure —
            // most often "You can only add N to the cart".
            throw new Error(data.description || data.message || 'Could not add to cart');
          }
          return data;
        });
      })
      .then(function (data) {
        refreshFromResponse(data);
        return fetchCartState().then(function () {
          setBusy(button, false);
          if (button) {
            var label = button.querySelector('[data-label]');
            if (label) {
              var original = label.textContent;
              label.textContent = 'Added';
              window.setTimeout(function () { label.textContent = original; }, 1600);
            }
          }
          openDrawer();
          return data;
        });
      })
      .catch(function (err) {
        setBusy(button, false);
        toast(err.message || 'Something went wrong', true);
      });
  }

  function changeLine(line, quantity) {
    drawerLoading(true);
    pageLoading(true);

    return fetch(url('/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity, sections: allCartSectionIds().join(',') })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Could not update the cart');
          return data;
        });
      })
      .then(function (data) {
        refreshFromResponse(data);
        paintCartCount(data.item_count);
        document.dispatchEvent(new CustomEvent('lux:cart:update', { detail: { cart: data } }));
        drawerLoading(false);
        pageLoading(false);
        return data;
      })
      .catch(function (err) {
        drawerLoading(false);
        pageLoading(false);
        toast(err.message || 'Something went wrong', true);
        return fetchDrawer();
      });
  }

  /* ---------------------------------------------------------------------
     Quick view
     --------------------------------------------------------------------- */

  var quickViewReleaseTrap = null;

  function modalEl() { return document.querySelector('[data-lux-modal]'); }

  function ensureModal() {
    var existing = modalEl();
    if (existing) return existing;

    var modal = document.createElement('div');
    modal.className = 'lux-modal';
    modal.setAttribute('data-lux-modal', '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Product quick view');
    // The close button is a sibling of .lux-modal__panel (both inside
    // .lux-modal__frame), not a child of it — the panel scrolls its own
    // content (overflow-y: auto), which would clip and swallow clicks on
    // anything positioned outside its box, including a close button
    // floating above its corner. Keeping .lux-modal__frame as the
    // positioning/animation context lets the close button track the
    // panel's actual rendered box (frame is sized to match it) without
    // being subject to the panel's own scroll clipping.
    modal.innerHTML =
      '<div class="lux-modal__backdrop" data-lux-modal-close></div>' +
      '<div class="lux-modal__frame">' +
        '<button type="button" class="lux-modal__close" data-lux-modal-close aria-label="Close quick view">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" aria-hidden="true">' +
            '<path d="M5 5l14 14M19 5L5 19" stroke-linecap="square"/>' +
          '</svg>' +
        '</button>' +
        '<div class="lux-modal__panel">' +
          '<div data-lux-modal-body></div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(modal);
    return modal;
  }

  function openQuickView(productUrl) {
    var modal = ensureModal();
    var bodyEl = modal.querySelector('[data-lux-modal-body]');

    bodyEl.innerHTML =
      '<div class="lux-modal__loading">' +
        '<svg class="lux-icon lux-icon--spin" width="26" height="26" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
          '<circle cx="12" cy="12" r="8.5" stroke-opacity="0.25"/>' +
          '<path d="M20.5 12a8.5 8.5 0 0 0-8.5-8.5" stroke-linecap="round"/>' +
        '</svg>' +
        '<span class="lux-visually-hidden">Loading</span>' +
      '</div>';

    // The modal may have just been created this tick (ensureModal above) —
    // adding .is-open in the same tick would apply it before the browser
    // ever paints the "closed" starting state, so the transition has
    // nothing to animate from and just snaps open instead of easing in.
    // Two rAFs guarantee a paint has happened in between.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        modal.classList.add('is-open');
      });
    });

    if (window.LuxUI) {
      window.LuxUI.lockScroll();
      quickViewReleaseTrap = window.LuxUI.trapFocus(modal, closeQuickView);
    }

    var sep = productUrl.indexOf('?') === -1 ? '?' : '&';

    fetch(productUrl + sep + 'view=quick-view', { headers: { 'Accept': 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Quick view unavailable');
        return r.text();
      })
      .then(function (html) {
        bodyEl.innerHTML = html;
        initQuickViewGallery(bodyEl);
        var focusTarget = bodyEl.querySelector('button, a, input');
        if (focusTarget) focusTarget.focus();
      })
      .catch(function () {
        // Never trap the customer in a broken modal — send them to the product.
        window.location.href = productUrl;
      });
  }

  function closeQuickView() {
    var modal = modalEl();
    if (!modal || !modal.classList.contains('is-open')) return;

    modal.classList.remove('is-open');
    if (quickViewReleaseTrap) { quickViewReleaseTrap(); quickViewReleaseTrap = null; }
    if (window.LuxUI) window.LuxUI.unlockScroll();

    // Matches the panel's close transition (assets/lux-product.css) plus a
    // small buffer — long enough that the content isn't wiped mid-animation,
    // short enough that a fast reopen doesn't visibly flash empty markup.
    window.setTimeout(function () {
      var bodyEl = modal.querySelector('[data-lux-modal-body]');
      if (bodyEl && !modal.classList.contains('is-open')) bodyEl.innerHTML = '';
    }, 320);
  }

  /* ---------------------------------------------------------------------
     Quick view gallery — native scroll-snap does the swiping; this just
     keeps the dots in sync and provides click targets (dots, arrows) that
     scroll the track. No drag logic, no animation loop.
     --------------------------------------------------------------------- */

  function galleryTrack(root) { return root.querySelector('[data-lux-qv-track]'); }

  function gallerySlides(root) { return root.querySelectorAll('[data-lux-qv-slide]'); }

  function syncGalleryDots(root) {
    var track = galleryTrack(root);
    var dots = root.querySelectorAll('[data-lux-qv-dot]');
    if (!track || !dots.length || !track.clientWidth) return;

    var index = Math.round(track.scrollLeft / track.clientWidth);
    dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === index);
    });
  }

  function scrollGalleryTo(root, index, smooth) {
    var track = galleryTrack(root);
    var slides = gallerySlides(root);
    if (!track || !slides.length) return;

    index = Math.max(0, Math.min(index, slides.length - 1));
    track.scrollTo({ left: index * track.clientWidth, behavior: smooth === false ? 'auto' : 'smooth' });
  }

  function scrollGalleryToMediaId(root, mediaId) {
    if (!mediaId) return;
    var slides = gallerySlides(root);
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
        scrollGalleryTo(root, i);
        return;
      }
    }
  }

  function initQuickViewGallery(root) {
    var track = galleryTrack(root);
    if (!track) return;

    var gallery = root.querySelector('[data-lux-qv-gallery]');
    var initialId = gallery ? gallery.getAttribute('data-initial-media-id') : '';

    if (initialId) {
      var slides = gallerySlides(root);
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === initialId && i > 0 && track.clientWidth) {
          track.scrollLeft = i * track.clientWidth;
          break;
        }
      }
    }

    syncGalleryDots(root);

    var syncTimer = null;
    track.addEventListener('scroll', function () {
      window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(function () { syncGalleryDots(root); }, 80);
    }, { passive: true });
  }

  /* ---------------------------------------------------------------------
     Quick view variant picker
     --------------------------------------------------------------------- */

  function readVariants(root) {
    var script = root.querySelector('[data-lux-qv-variants]');
    if (!script) return [];
    try { return JSON.parse(script.textContent); } catch (e) { return []; }
  }

  function selectedOptions(root) {
    var values = [];
    root.querySelectorAll('[data-lux-option-group]').forEach(function (group) {
      var active = group.querySelector('[data-lux-option-value][aria-pressed="true"]');
      values.push(active ? active.getAttribute('data-value') : null);
    });
    return values;
  }

  function matchVariant(variants, chosen) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var ok = true;
      for (var j = 0; j < chosen.length; j++) {
        if (chosen[j] === null) continue;
        if (v.options[j] !== chosen[j]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  function updateQuickView(root) {
    var variants = readVariants(root);
    var chosen = selectedOptions(root);
    var variant = matchVariant(variants, chosen);

    var idInput = root.querySelector('[data-lux-qv-variant-id]');
    var submit = root.querySelector('[data-lux-qv-submit]');
    var buyNowBtn = root.querySelector('[data-lux-qv-buynow]');
    var label = submit ? submit.querySelector('[data-label]') : null;
    var priceEl = root.querySelector('[data-lux-qv-price] .lux-price');
    var stockEl = root.querySelector('[data-lux-qv-stock]');

    // Mark combinations that do not exist or are sold out.
    root.querySelectorAll('[data-lux-option-group]').forEach(function (group, gi) {
      var selectedLabel = group.querySelector('[data-lux-option-selected]');
      var active = group.querySelector('[data-lux-option-value][aria-pressed="true"]');
      if (selectedLabel && active) selectedLabel.textContent = active.getAttribute('data-value');

      group.querySelectorAll('[data-lux-option-value]').forEach(function (btn) {
        var probe = chosen.slice();
        probe[gi] = btn.getAttribute('data-value');
        var candidate = matchVariant(variants, probe);
        btn.setAttribute('data-available', candidate && candidate.available ? 'true' : 'false');
      });
    });

    if (!variant) {
      if (submit) submit.disabled = true;
      if (buyNowBtn) buyNowBtn.disabled = true;
      if (label) label.textContent = 'Unavailable';
      return;
    }

    if (idInput) idInput.value = variant.id;

    if (submit) submit.disabled = !variant.available;
    if (buyNowBtn) buyNowBtn.disabled = !variant.available;
    if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';

    if (priceEl) {
      var current = priceEl.querySelector('[data-lux-price]');
      var compare = priceEl.querySelector('[data-lux-compare-price]');
      var symbolSource = current ? current.textContent.trim() : '';
      var prefix = symbolSource.replace(/[\d.,\s]/g, '') || '';

      if (current) current.textContent = prefix + money(variant.price);

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceEl.classList.add('lux-price--on-sale');
        if (compare) {
          compare.textContent = prefix + money(variant.compare_at_price);
          compare.hidden = false;
        }
      } else {
        priceEl.classList.remove('lux-price--on-sale');
        if (compare) compare.hidden = true;
      }
    }

    if (stockEl) {
      if (!variant.available) {
        stockEl.innerHTML = '<span class="lux-stock lux-stock--out"><span class="lux-stock__dot"></span>Sold out</span>';
      } else {
        stockEl.innerHTML = '<span class="lux-stock"><span class="lux-stock__dot"></span>In stock</span>';
      }
    }

    // Highlight the gallery slide matching this variant's image, if any —
    // the image is already loaded in the gallery, so this just scrolls to
    // it rather than swapping a src (no flash, no extra request).
    if (variant.featured_image && variant.featured_image.id) {
      scrollGalleryToMediaId(root, variant.featured_image.id);
    }
  }

  /* ---------------------------------------------------------------------
     Buy now — adds the current form's variant/quantity to the cart, then
     sends the customer straight to checkout instead of opening the drawer.
     --------------------------------------------------------------------- */

  function buyNow(form, button) {
    var data = new FormData(form);
    setBusy(button, true, 'Redirecting…');

    return fetch(url('/cart/add.js'), {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: data
    })
      .then(function (res) {
        return res.json().then(function (payload) {
          if (!res.ok || payload.status) {
            throw new Error(payload.description || payload.message || 'Could not add to cart');
          }
          return payload;
        });
      })
      .then(function () {
        window.location.href = url('/checkout');
      })
      .catch(function (err) {
        setBusy(button, false);
        toast(err.message || 'Something went wrong', true);
      });
  }

  /* ---------------------------------------------------------------------
     Delegated events
     --------------------------------------------------------------------- */

  document.addEventListener('click', function (e) {

    /* ---- Cart drawer open ---- */
    var cartToggle = e.target.closest('[data-lux-cart-toggle]');
    if (cartToggle) {
      if (document.body.hasAttribute('data-lux-cart-drawer-disabled')) return; // follow the link
      if (!drawerEl()) return;
      e.preventDefault();
      openDrawer();
      return;
    }

    if (e.target.closest('[data-lux-cart-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // Overlay click closes whichever of the two is open.
    if (e.target.closest('[data-lux-overlay]') && body.classList.contains('lux-cart-open')) {
      closeDrawer();
      return;
    }

    /* ---- Quick add ---- */
    var quickAdd = e.target.closest('[data-lux-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      e.stopPropagation();
      var variantId = quickAdd.getAttribute('data-variant-id');
      if (!variantId) return;
      addToCart({ items: [{ id: Number(variantId), quantity: 1 }] }, quickAdd);
      return;
    }

    /* ---- Quick view ---- */
    var qv = e.target.closest('[data-lux-quick-view]');
    if (qv) {
      e.preventDefault();
      e.stopPropagation();
      openQuickView(qv.getAttribute('data-lux-quick-view'));
      return;
    }

    if (e.target.closest('[data-lux-modal-close]')) {
      e.preventDefault();
      closeQuickView();
      return;
    }

    /* ---- Quick view: view full details — close, then navigate ---- */
    var viewDetails = e.target.closest('[data-lux-qv-view-details]');
    if (viewDetails) {
      e.preventDefault();
      var detailsHref = viewDetails.getAttribute('href');
      closeQuickView();
      window.setTimeout(function () { window.location.href = detailsHref; }, 200);
      return;
    }

    /* ---- Quick view: buy now ---- */
    var buyNowBtn = e.target.closest('[data-lux-qv-buynow]');
    if (buyNowBtn) {
      e.preventDefault();
      if (buyNowBtn.disabled) return;
      var buyForm = buyNowBtn.closest('form');
      if (buyForm) buyNow(buyForm, buyNowBtn);
      return;
    }

    /* ---- Quick view gallery: dots ---- */
    var galleryDot = e.target.closest('[data-lux-qv-dot]');
    if (galleryDot) {
      e.preventDefault();
      var dotRoot = galleryDot.closest('[data-lux-quick-view-root]');
      if (dotRoot) scrollGalleryTo(dotRoot, parseInt(galleryDot.getAttribute('data-index'), 10));
      return;
    }

    /* ---- Quick view gallery: prev / next ---- */
    var galleryNav = e.target.closest('[data-lux-qv-nav]');
    if (galleryNav) {
      e.preventDefault();
      var navRoot = galleryNav.closest('[data-lux-quick-view-root]');
      var navTrack = navRoot ? galleryTrack(navRoot) : null;
      if (navRoot && navTrack && navTrack.clientWidth) {
        var currentIndex = Math.round(navTrack.scrollLeft / navTrack.clientWidth);
        var dir = galleryNav.getAttribute('data-lux-qv-nav') === 'next' ? 1 : -1;
        scrollGalleryTo(navRoot, currentIndex + dir);
      }
      return;
    }

    /* ---- Quick view options ---- */
    var optionBtn = e.target.closest('[data-lux-option-value]');
    if (optionBtn) {
      e.preventDefault();
      var group = optionBtn.closest('[data-lux-option-group]');
      group.querySelectorAll('[data-lux-option-value]').forEach(function (b) {
        b.setAttribute('aria-pressed', b === optionBtn ? 'true' : 'false');
      });
      updateQuickView(optionBtn.closest('[data-lux-quick-view-root]'));
      return;
    }

    /* ---- Quantity steppers (quick view + product page) ---- */
    var qtyBtn = e.target.closest('[data-lux-qty]');
    if (qtyBtn) {
      e.preventDefault();
      var input = qtyBtn.parentElement.querySelector('[data-lux-qty-input]');
      if (!input) return;
      var next = (parseInt(input.value, 10) || 1) + parseInt(qtyBtn.getAttribute('data-lux-qty'), 10);
      input.value = Math.max(1, next);
      return;
    }

    /* ---- Quantity steppers (cart lines — drawer and cart page) ---- */
    var cartQty = e.target.closest('[data-lux-cart-qty]');
    if (cartQty) {
      e.preventDefault();
      var line = parseInt(cartQty.getAttribute('data-line'), 10);
      var lineInput = document.querySelector('[data-lux-cart-qty-input][data-line="' + line + '"]');
      var currentQty = lineInput ? (parseInt(lineInput.value, 10) || 0) : 0;
      var delta = parseInt(cartQty.getAttribute('data-lux-cart-qty'), 10);
      changeLine(line, Math.max(0, currentQty + delta));
      return;
    }

    /* ---- Remove line ---- */
    var removeBtn = e.target.closest('[data-lux-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      changeLine(parseInt(removeBtn.getAttribute('data-line'), 10), 0);
      return;
    }

    /* ---- Card swatches ---- */
    var swatch = e.target.closest('[data-lux-swatch]');
    if (swatch) {
      e.preventDefault();
      e.stopPropagation();

      var card = swatch.closest('[data-lux-card]');
      var group = swatch.closest('.lux-swatches');

      group.querySelectorAll('[data-lux-swatch]').forEach(function (b) {
        b.setAttribute('aria-pressed', b === swatch ? 'true' : 'false');
      });

      var img = card && card.querySelector('.lux-card__img--primary');
      var newSrc = swatch.getAttribute('data-variant-image');

      if (img && newSrc) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = newSrc;
      }

      // Point the card's links at the chosen variant.
      var variantUrl = swatch.getAttribute('data-variant-url');
      if (card && variantUrl) {
        card.querySelectorAll('a[href]').forEach(function (a) { a.href = variantUrl; });
        var qvBtn = card.querySelector('[data-lux-quick-view]');
        if (qvBtn) qvBtn.setAttribute('data-lux-quick-view', variantUrl);
      }
    }
  });

  /* ---- Typed quantity in the drawer / cart page ---- */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-lux-cart-qty-input]');
    if (!input) return;

    var line = parseInt(input.getAttribute('data-line'), 10);
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(line, qty);
  });

  /* ---------------------------------------------------------------------
     Any form that adds to the cart — quick view, the product page, and
     Dawn's own product form

     Dawn's <product-form> attaches its handler to the form element and always
     preventDefaults, so a listener on the bubble phase would add the item
     twice. Capturing on document runs first, and stopPropagation keeps the
     event from ever reaching Dawn's handler — one add, one drawer.
     --------------------------------------------------------------------- */

  function addFromForm(form, submitButton) {
    var data = new FormData(form);
    data.append('sections', allCartSectionIds().join(','));
    data.append('sections_url', window.location.pathname);

    setBusy(submitButton, true, 'Adding…');

    return fetch(url('/cart/add.js'), {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: data
    })
      .then(function (res) {
        return res.json().then(function (payload) {
          if (!res.ok || payload.status) {
            throw new Error(payload.description || payload.message || 'Could not add to cart');
          }
          return payload;
        });
      })
      .then(function (payload) {
        refreshFromResponse(payload);
        return fetchCartState().then(function () {
          setBusy(submitButton, false);
          openDrawer();
          return payload;
        });
      })
      .catch(function (err) {
        setBusy(submitButton, false);
        toast(err.message || 'Something went wrong', true);
      });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;

    // No drawer on the page (or the merchant switched the cart back to
    // Shopify's own UI) — leave the native behaviour alone.
    if (!drawerEl()) return;
    if (document.querySelector('cart-drawer, cart-notification')) return;

    e.preventDefault();
    e.stopPropagation();

    var submitButton = form.querySelector('[data-lux-qv-submit]') ||
                       form.querySelector('[data-lux-pdp-submit]') ||
                       form.querySelector('[type="submit"]');

    var isQuickView = !!form.closest('[data-lux-quick-view-root]');

    addFromForm(form, submitButton).then(function () {
      if (isQuickView) closeQuickView();
    });
  }, true);

  /* ---- Escape closes the quick view (the drawer is handled by trapFocus) ---- */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var modal = modalEl();
    if (modal && modal.classList.contains('is-open')) closeQuickView();
  });

  /* ---------------------------------------------------------------------
     Init
     --------------------------------------------------------------------- */

  function init() {
    // Keep the count honest after a back/forward navigation from a cached page.
    if (drawerEl()) fetchCartState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) { fetchDrawer(); fetchCartState(); }
  });

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-lux-cart-drawer]')) closeDrawer();
  });

  window.LuxCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: fetchDrawer,
    add: addToCart,
    toast: toast
  };
})();
